import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CrwdOrgTab } from '@crewdle/ui-kit';

import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { landingPathFor, OrgSwitcherService } from 'src/app/services/org-switcher/org-switcher.service';
import { UserService } from 'src/app/services/user/user.service';
import { TabPageNames, tabPageTitleDetail } from './org-tab-title';

/** v2: tabs are places inside orgs (several per org), in strip order, plus the active one. */
const TABS_V2_KEY = 'orgTabsV2';
/** v1 (2026-10-21): one tab per org — ids, and the last path per org. Migrated once into v2. */
const TABS_KEY = 'orgTabs';
const LAST_PATHS_KEY = 'orgTabsLastPaths';
/** Pages that only make sense where they were opened — a switch back lands on the section home instead. */
const NOT_RESTORED_RE = /^\/(product-disabled|region-unavailable|upgrade)(?=$|[/?#])/;

/**
 * One tab: a place inside an organization. `url` null = the org's home;
 * `title` = last known page name, `titleResolved` = it is the page's real name
 * (not the section stand-in shown while the rail's lists were loading).
 */
export interface OrgTabState {
  id: string;
  orgId: string;
  url: string | null;
  title?: string;
  titleResolved?: boolean;
}

/** A strip tab as the kit shows it, plus the org it belongs to (counters are per org). */
export type OrgStripTab = CrwdOrgTab & { orgId: string };

const EMPTY_NAMES: TabPageNames = { conversations: new Map(), chats: new Map(), topics: new Map() };

/**
 * The tabs at the top of the app, like browser tabs (Vincent, 2026-10-21; and
 * 2026-10-22: "open multiple tabs of the same org… 2 tabs with 2 different
 * agents" + drag-and-drop reordering). A tab is a place inside an
 * organization: switching to a tab of another org is OrgSwitcherService's
 * in-place switch, to a tab of the same org a plain navigation (no re-bind).
 * Per device and per user (localStorage). An org is "open" while at least one
 * of its tabs is — per-org services (counters) key on `openOrgIds`.
 */
@Injectable({ providedIn: 'root' })
export class OrgTabsService {
  private readonly localStorage = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly switcher = inject(OrgSwitcherService);
  private readonly translate = inject(TranslateService);
  private readonly user = inject(UserService);

  private readonly state = signal<OrgTabState[]>([]);
  /** The tab you are in. */
  public readonly activeId = signal<string>('');
  /** A tab of another org being switched to — the current org catches up when the switch lands. */
  private pendingId: string | null = null;
  private readonly names = signal<TabPageNames>(EMPTY_NAMES);
  private loadedFor: string | null = null;
  private navSub: Subscription | null = null;

  private readonly memberIds = computed(() => this.switcher.directory().map((org) => org.id));

  /** Tabs of orgs you still belong to (all of them while the directory is loading). */
  private readonly visible = computed(() =>
    this.state().filter((tab) => !this.memberIds().length || this.memberIds().includes(tab.orgId)));

  /**
   * The strip: company name + logo, and the page each tab shows. A background
   * tab shows the name frozen while it was active; the tab you are in shows
   * the live name once resolved, its frozen name meanwhile.
   */
  public readonly tabs = computed<OrgStripTab[]>(() => {
    const byId = new Map(this.switcher.directory().map((org) => [org.id, org]));
    const active = this.activeId();
    return this.visible().map((tab) => {
      const org = byId.get(tab.orgId);
      let title = tab.title ?? null;
      if (tab.id === active || title === null) {
        const live = this.liveTitle(tab);
        if (live.resolved || title === null || !tab.titleResolved) title = live.title ?? title;
      }
      return {
        id: tab.id,
        orgId: tab.orgId,
        name: org?.name || tab.orgId,
        ...(title ? { title } : {}),
        ...(org?.logoUrl ? { logoUrl: org.logoUrl } : {}),
      };
    });
  });

  /** Orgs with at least one open tab, in strip order. */
  public readonly openOrgIds = computed(() => [...new Set(this.visible().map((tab) => tab.orgId))]);

  constructor() {
    // The tab you are in always belongs to the org you are in.
    effect(() => {
      const current = this.switcher.currentOrgId();
      this.ensureLoaded();
      if (!current || this.pendingId) return;
      const tabs = this.state();
      const active = tabs.find((tab) => tab.id === this.activeId());
      if (active?.orgId === current) return;
      const sameOrg = tabs.find((tab) => tab.orgId === current);
      if (sameOrg) {
        this.activate(sameOrg.id);
        return;
      }
      const created: OrgTabState = { id: newTabId(), orgId: current, url: null };
      this.persist(insertAfter(tabs, active?.id ?? null, created), created.id);
    });
    // The active tab remembers where you are and what the page is called.
    this.navSub = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const path = e.urlAfterRedirects;
      if (NOT_RESTORED_RE.test(path)) return;
      const target = this.trackedTab();
      if (!target) return;
      const live = this.liveTitle({ ...target, url: path });
      if (path !== target.url) {
        // A new page: its own name, or the section until the lists name it.
        this.update(target.id, { url: path, title: live.title ?? undefined, titleResolved: live.resolved });
      } else if (live.resolved && live.title) {
        this.update(target.id, { title: live.title, titleResolved: true });
      }
    });
    // Names arrive after the page: freeze the ACTIVE tab's real name for when it goes to the background.
    // Only a resolved name is written — never a stand-in, never during a switch (the lists are changing org).
    effect(() => {
      this.names();
      if (this.pendingId) return;
      const active = this.state().find((tab) => tab.id === this.activeId());
      if (!active || active.orgId !== this.switcher.currentOrgId()) return;
      const live = this.liveTitle(active);
      if (live.resolved && live.title && (live.title !== active.title || !active.titleResolved)) {
        this.update(active.id, { title: live.title, titleResolved: true });
      }
    });
    inject(DestroyRef).onDestroy(() => this.navSub?.unsubscribe());
  }

  /** The rail's names for the org you are in (agents, projects, spaces, chats, topics). */
  public setPageNames(names: TabPageNames): void {
    this.names.set(names);
  }

  /** Go to a tab — back to where you were in it. */
  public select(tabId: string): void {
    const tab = this.state().find((t) => t.id === tabId);
    if (!tab || tab.id === this.activeId()) return;
    if (tab.orgId === this.switcher.currentOrgId()) {
      this.activate(tab.id);
      void this.router.navigateByUrl(tab.url ?? landingPathFor(''));
      return;
    }
    void this.switchToTab(tab);
  }

  /** Close a tab; closing the one you are in moves to its neighbour (right, else left). The last tab stays. */
  public close(tabId: string): void {
    const tabs = this.state();
    if (tabs.length <= 1) return;
    const at = tabs.findIndex((t) => t.id === tabId);
    if (at < 0) return;
    const next = tabs.filter((t) => t.id !== tabId);
    const wasActive = tabId === this.activeId();
    this.persist(next, this.activeId());
    if (wasActive) this.select(next[Math.min(at, next.length - 1)].id);
  }

  /** "+": a new tab of `orgId` on its home, right after the tab you are in, and go there. */
  public open(orgId: string): void {
    const created: OrgTabState = { id: newTabId(), orgId, url: null };
    this.persist(insertAfter(this.state(), this.activeId(), created), this.activeId());
    this.select(created.id);
  }

  /** Drag and drop / Alt+arrows: move a tab to another place in the strip. */
  public move(tabId: string, toIndex: number): void {
    const tabs = [...this.state()];
    const from = tabs.findIndex((t) => t.id === tabId);
    if (from < 0) return;
    const [tab] = tabs.splice(from, 1);
    tabs.splice(Math.max(0, Math.min(toIndex, tabs.length)), 0, tab);
    this.persist(tabs, this.activeId());
  }

  private async switchToTab(tab: OrgTabState): Promise<void> {
    this.pendingId = tab.id;
    try {
      await this.switcher.switchTo(tab.orgId, tab.url ?? undefined);
    } finally {
      this.pendingId = null;
      if (this.switcher.currentOrgId() === tab.orgId && this.state().some((t) => t.id === tab.id)) this.activate(tab.id);
      // Re-run the invariant now that the switch settled (it was held while pending).
      this.state.set([...this.state()]);
    }
  }

  /** The tab navigation belongs to: the one being switched to once its org has arrived, else the active one in this org. */
  private trackedTab(): OrgTabState | undefined {
    const current = this.switcher.currentOrgId();
    if (!current) return undefined;
    const id = this.pendingId ?? this.activeId();
    const tab = this.state().find((t) => t.id === id);
    return tab?.orgId === current ? tab : undefined;
  }

  private liveTitle(tab: OrgTabState): { title: string | null; resolved: boolean } {
    const names = tab.orgId === this.switcher.currentOrgId() ? this.names() : EMPTY_NAMES;
    return tabPageTitleDetail(tab.url, names, (key) => this.translate.instant(key));
  }

  private activate(tabId: string): void {
    this.activeId.set(tabId);
    this.save();
  }

  private update(tabId: string, patch: Partial<OrgTabState>): void {
    const tabs = this.state();
    const at = tabs.findIndex((t) => t.id === tabId);
    if (at < 0) return;
    const merged = { ...tabs[at], ...patch };
    if (merged.url === tabs[at].url && merged.title === tabs[at].title && merged.titleResolved === tabs[at].titleResolved) return;
    const next = [...tabs];
    next[at] = merged;
    this.persist(next, this.activeId());
  }

  private key(base: string): string {
    return `${base}:${this.user.user?.uid ?? 'anon'}`;
  }

  private ensureLoaded(): void {
    const uid = this.user.user?.uid ?? null;
    if (!uid || this.loadedFor === uid) return;
    this.loadedFor = uid;
    const v2 = parseV2(this.localStorage.getItem(this.key(TABS_V2_KEY)));
    if (v2) {
      this.state.set(v2.tabs);
      this.activeId.set(v2.activeId);
      return;
    }
    // v1 → v2: one tab per previously open org, on the page last seen there.
    const lastPaths = parseRecord(this.localStorage.getItem(this.key(LAST_PATHS_KEY)));
    const migrated = parseList(this.localStorage.getItem(this.key(TABS_KEY)))
      .map((orgId) => ({ id: newTabId(), orgId, url: lastPaths[orgId] ?? null }));
    const current = this.switcher.currentOrgId();
    this.persist(migrated, migrated.find((t) => t.orgId === current)?.id ?? '');
  }

  private persist(tabs: OrgTabState[], activeId: string): void {
    this.state.set(tabs);
    this.activeId.set(activeId);
    this.save();
  }

  private save(): void {
    if (!this.loadedFor) return;
    this.localStorage.setItem(this.key(TABS_V2_KEY), JSON.stringify({ tabs: this.state(), activeId: this.activeId() }));
  }
}

let seq = 0;
function newTabId(): string {
  seq += 1;
  return `t${Date.now().toString(36)}${seq.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function insertAfter(tabs: OrgTabState[], afterId: string | null, tab: OrgTabState): OrgTabState[] {
  const at = afterId ? tabs.findIndex((t) => t.id === afterId) : -1;
  const next = [...tabs];
  next.splice(at < 0 ? next.length : at + 1, 0, tab);
  return next;
}

function parseV2(raw: string | null): { tabs: OrgTabState[]; activeId: string } | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || !Array.isArray(value.tabs)) return null;
    const tabs = value.tabs
      .filter((t: Partial<OrgTabState>) => typeof t?.id === 'string' && typeof t?.orgId === 'string')
      .map((t: OrgTabState) => ({
        id: t.id,
        orgId: t.orgId,
        url: typeof t.url === 'string' ? t.url : null,
        ...(typeof t.title === 'string' ? { title: t.title } : {}),
        ...(t.titleResolved === true ? { titleResolved: true } : {}),
      }));
    return { tabs, activeId: typeof value.activeId === 'string' ? value.activeId : '' };
  } catch {
    return null;
  }
}

function parseList(raw: string | null): string[] {
  try {
    const value = JSON.parse(raw || '[]');
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function parseRecord(raw: string | null): Record<string, string> {
  try {
    const value = JSON.parse(raw || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}
